<template>
  <header class="fixed-top w-100">
    <!-- =====================================================
         MARQUEE (derecha → izquierda, infinita)
         ===================================================== -->
    <div class="marquee" aria-label="Novedades">
      <div class="marquee-track">
        <!-- 4 copias del mismo bloque: el track siempre es más ancho que la
             pantalla y el salto de -25% (100/4) queda invisible -->
        <div
          v-for="group in 4"
          :key="group"
          class="marquee-group"
          :aria-hidden="group > 1 ? 'true' : null"
        >
          <template
            v-for="(item, index) in marqueeItems"
            :key="item + index"
          >
            <span class="marquee-item">{{ item }}</span>

            <!-- Punto: separador entre ítems -->
            <span
              class="marquee-sep"
              aria-hidden="true"
            />
          </template>
        </div>
      </div>
    </div>

    <!-- =====================================================
         NAVBAR
         ===================================================== -->
    <nav
      class="navbar px-3"
      :class="{
        'navbar-scrolled': scrolled || showSearch || isMenuOpen
      }"
    >
      <div class="container py-3">
        <!-- =================================================
             DESKTOP
             ================================================= -->
        <div
          class="d-none d-lg-flex align-items-center w-100 desktop-navbar"
        >
          <!-- HAMBURGUESA: abre el menú a pantalla completa -->
          <button
            type="button"
            class="menu-toggle"
            :class="{ 'is-active': isMenuOpen }"
            :aria-expanded="isMenuOpen"
            aria-label="Abrir o cerrar menú"
            @click="toggleMenu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <!-- ACCIONES DERECHA -->
          <div class="nav-actions">
            <!-- BUSCAR -->
            <button
              type="button"
              class="nav-icon-btn search-trigger"
              aria-label="Buscar productos"
              :aria-expanded="showSearch"
              @click="toggleSearch"
            >
              <MinimalIcon name="search" :size="19" />
            </button>

            <!-- CUENTA -->
            <router-link
              :to="isAuthenticated ? '/configuracion' : '/login'"
              class="account-btn"
            >
              <MinimalIcon name="user" :size="18" />

              <span>
                {{ isAuthenticated ? username : 'INGRESAR' }}
              </span>
            </router-link>
          </div>
        </div>

        <!-- =================================================
             MOBILE
             ================================================= -->
        <div
          class="d-flex d-lg-none align-items-center w-100 mobile-actions"
        >
          <!-- MENÚ (izquierda) -->
          <button
            type="button"
            class="menu-toggle mobile-menu-toggle"
            :class="{ 'is-active': isMenuOpen }"
            @click="toggleMenu"
            aria-label="Abrir o cerrar menú"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <!-- ACCIONES (derecha) -->
          <div class="mobile-actions-right">
            <!-- BUSCAR -->
            <button
              type="button"
              class="nav-icon-btn"
              @click="toggleSearch"
              aria-label="Buscar productos"
              :aria-expanded="showSearch"
            >
              <MinimalIcon name="search" :size="19" />
            </button>

            <!-- CUENTA -->
            <router-link
              :to="isAuthenticated ? '/configuracion' : '/login'"
              class="nav-icon-btn"
              aria-label="Mi cuenta"
            >
              <MinimalIcon name="user" :size="19" />
            </router-link>
          </div>
        </div>
      </div>
    </nav>

    <!-- =====================================================
         PANEL DE BÚSQUEDA
         ===================================================== -->
    <transition name="search-panel">
      <div
        v-if="showSearch"
        class="search-menu"
        @click.self="closeSearch"
      >
        <div class="search-menu-container">
          <!-- BUSCADOR -->
          <form
            class="site-menu-search"
            @submit.prevent="handleSearch"
          >
            <MinimalIcon
              name="search"
              :size="22"
              class="site-menu-search-icon"
            />

            <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              inputmode="search"
              enterkeyhint="search"
              class="site-menu-search-input"
              placeholder="Buscar genéticas..."
              autocomplete="off"
              aria-label="Buscar productos"
            />

            <button
              type="button"
              class="site-menu-search-close"
              aria-label="Cerrar búsqueda"
              @click="closeSearch"
            >
              <MinimalIcon name="close" :size="20" />
            </button>
          </form>

          <!-- AYUDA -->
          <p
            v-if="searchQuery.trim().length < 2"
            class="search-menu-helper"
          >
            Escribí al menos 2 caracteres para buscar.
          </p>

          <!-- RESULTADOS -->
          <p v-else-if="searchLoading" class="search-menu-helper" role="status">
            Buscando productos...
          </p>

          <p v-else-if="searchError" class="search-menu-empty" role="alert">
            No pudimos buscar ahora. Intentá de nuevo.
          </p>

          <div
            v-else-if="products.length > 0"
            class="search-menu-results"
          >
            <p class="search-menu-sub">
              Resultados
            </p>

            <ul class="search-menu-list">
              <li
                v-for="product in products"
                :key="product.id"
              >
                <router-link
                  :to="`/producto/${product.id}`"
                  class="search-menu-item"
                  @click="closeSearch"
                >
                  <span class="search-menu-item-media">
                    <img
                      v-if="product.image"
                      :src="product.image"
                      :alt="product.name"
                    />
                  </span>

                  <span class="search-menu-item-name">{{ product.name }}</span>

                  <MinimalIcon
                    name="arrow"
                    :size="16"
                    class="search-menu-item-arrow"
                  />
                </router-link>
              </li>
            </ul>

            <button
              type="button"
              class="search-menu-all"
              @click="viewAllResults"
            >
              Ver todos los resultados

              <MinimalIcon name="arrow" :size="15" />
            </button>
          </div>

          <!-- SIN RESULTADOS -->
          <p
            v-else
            class="search-menu-empty"
          >
            No encontramos productos para
            <strong>“{{ searchQuery }}”</strong>
          </p>
        </div>
      </div>
    </transition>

    <!-- =====================================================
         MENÚ MOBILE
         ===================================================== -->
    <transition name="slide-down">
      <div
        v-if="isMenuOpen"
        class="mobile-menu"
        @click.self="closeMenu"
      >
        <div
          class="mobile-menu-container"
          :class="{ 'mobile-menu-container--in': revealMenu }"
        >
          <!-- Marca de agua: logo gigante, inclinado y tenue abajo a la izquierda -->
          <span class="site-menu-watermark-layer" aria-hidden="true">
            <img
              src="@/assets/images/logotipo.png"
              alt=""
              class="site-menu-watermark"
            />
          </span>

          <!-- NAVLINKS GRANDES + ITEMS CHICOS -->
          <nav class="site-menu-main" aria-label="Navegación principal">
            <ul class="site-menu-links">
              <li
                v-for="item in menuItems"
                :key="item.route"
              >
                <router-link
                  :to="item.route"
                  class="site-menu-link"
                  @click="closeMenu"
                >
                  {{ item.label }}
                </router-link>
              </li>
            </ul>

          </nav>

          <!-- PIE: TEXTO (IZQ) + ÚLTIMO PRODUCTO (DER) -->
          <div class="site-menu-bottom">
            <p class="site-menu-note text-lg-start text-center">
              Salimos a buscar el terpeno escondido en los genes: el aroma y el
              sabor son la huella que delata a cada genética antes del primer
              efecto.
            </p>

            <router-link
              v-if="latestProduct"
              :to="`/producto/${latestProduct.id}`"
              class="site-menu-latest"
              @click="closeMenu"
            >
              <span class="site-menu-latest-copy">
                <span class="site-menu-latest-kicker">Último producto</span>
                <span class="site-menu-latest-name">{{ latestProduct.name }}</span>
              </span>

              <img
                v-if="latestProduct.image"
                :src="latestProduct.image"
                :alt="latestProduct.name"
                class="site-menu-latest-img"
                loading="lazy"
              />
            </router-link>
            <!-- Skeleton mientras carga la último producto -->
            <div
              v-else
              class="site-menu-latest site-menu-latest--skeleton"
              aria-hidden="true"
            >
              <span class="site-menu-latest-copy">
                <span class="site-menu-skeleton site-menu-skeleton--kicker"></span>
                <span class="site-menu-skeleton site-menu-skeleton--name"></span>
              </span>

              <span class="site-menu-skeleton site-menu-skeleton--img"></span>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script setup>
import '@/assets/css/variables.css';

import {
  ref,
  computed,
  nextTick,
  watch,
  onMounted,
  onUnmounted
} from 'vue';

import { useRouter } from 'vue-router';

import MinimalIcon from '@/components/common/MinimalIcon.vue';
import { curtainOrigin, DURACION_MENU, destaparPantalla, pageCurtainPhase, taparPantalla } from '@/composables/usePageTransition';


/* =========================================================
   ROUTER
   ========================================================= */

const router = useRouter();


/* =========================================================
   NAVBAR
   ========================================================= */

const scrolled = ref(false);

const isMenuOpen = ref(false);

/* El menú entra dentro del recuadro que sube hasta el tope: misma animación
   que la carga de una página nueva. */
const revealMenu = computed(() =>
  pageCurtainPhase.value === 'revealing' && curtainOrigin.value === 'menu'
);


/* =========================================================
   USER
   ========================================================= */

const storedUsername = ref('');


const username = computed(() => {
  const value = storedUsername.value;

  if (!value) {
    return '';
  }

  if (value.includes('@')) {
    return value.split('@')[0];
  }

  return value;
});


const isAuthenticated = computed(() => {
  if (typeof window === 'undefined') {
    return false;
  }

  return localStorage.getItem('accessToken') !== null;
});


/* =========================================================
   MENU ITEMS
   ========================================================= */

const menuItems = [
  {
    label: 'Inicio',
    route: '/',
    icon: 'home'
  },
  {
    label: 'Catálogo',
    route: '/productos',
    icon: 'catalog'
  },
  {
    label: 'Galería',
    route: '/galeria',
    icon: 'gallery'
  },
  {
    label: 'Blog',
    route: '/blog',
    icon: 'blog'
  }
];



/* =========================================================
   MARQUEE
   ========================================================= */

const marqueeItems = [
  'Envíos discretos a todo el país',
  'Genética original de criador',
  'Fotoperiódicas y automáticas',
  'Puntos de venta oficiales'
];

/* =========================================================
   PRODUCTS
   ========================================================= */

const products = ref([]);


/* =========================================================
   SEARCH
   ========================================================= */

const searchQuery = ref('');

const showSearch = ref(false);

const searchInput = ref(null);
const searchLoading = ref(false);
const searchError = ref(false);
let searchTimer = null;
let searchRequestId = 0;

watch(searchQuery, queryValue => {
  clearTimeout(searchTimer);
  searchRequestId += 1;
  products.value = [];
  searchError.value = false;

  const query = queryValue.trim();
  if (!showSearch.value || query.length < 2) {
    searchLoading.value = false;
    return;
  }

  searchLoading.value = true;
  const requestId = searchRequestId;
  searchTimer = setTimeout(async () => {
    try {
      const { searchProducts } = await import('@/services/products');
      const results = await searchProducts(query);
      if (requestId === searchRequestId && showSearch.value) {
        products.value = results;
      }
    } catch (error) {
      if (requestId === searchRequestId && showSearch.value) {
        console.error('Error buscando productos:', error);
        searchError.value = true;
      }
    } finally {
      if (requestId === searchRequestId) searchLoading.value = false;
    }
  }, 250);
});


const toggleSearch = async () => {
  if (showSearch.value) {
    closeSearch();
    return;
  }

  isMenuOpen.value = false;

  showSearch.value = true;

  scrolled.value = true;

  await nextTick();

  searchInput.value?.focus();
};


const closeSearch = () => {
  showSearch.value = false;
  clearTimeout(searchTimer);
  searchRequestId += 1;
  searchLoading.value = false;
  searchError.value = false;
  products.value = [];
  searchQuery.value = '';

  if (
    !isMenuOpen.value &&
    window.scrollY <= 50
  ) {
    scrolled.value = false;
  }
};


const handleSearch = async () => {
  const query = searchQuery.value.trim();

  if (query.length < 2) {
    return;
  }

  closeSearch();

  await router.push({
    path: '/productos',
    query: { search: query }
  });
};


const viewAllResults = () => {
  handleSearch();
};


/* =========================================================
   MOBILE MENU
   ========================================================= */

const toggleMenu = async () => {
  if (isMenuOpen.value) {
    closeMenu();

    return;
  }

  closeSearch();

  /* La cortina sube y tapa; recién ahí se monta el menú, que entra dentro del
     recuadro que sube hasta el tope. No se mueve la página donde estaba. */
  const tapada = await taparPantalla({
    tapa: DURACION_MENU.tapa,
    origen: 'menu'
  });

  if (!tapada) return;

  isMenuOpen.value = true;

  scrolled.value =
    isMenuOpen.value ||
    window.scrollY > 50;

  destaparPantalla({
    salida: DURACION_MENU.salida,
    scrollAlTope: false
  });
};


const closeMenu = () => {
  isMenuOpen.value = false;

  if (
    !showSearch.value &&
    window.scrollY <= 50
  ) {
    scrolled.value = false;
  }
};



/* Último producto agregado: se pide la primera vez que se abre el menú y se
   guarda en localStorage con vencimiento, para no pegarle a la API cada vez */
const LATEST_CACHE_KEY = 'hf:latest-product';
const LATEST_CACHE_TTL = 6 * 60 * 60 * 1000; /* 6 horas */

const latestProduct = ref(null);

/* El menú muestra un skeleton mientras llega la último producto */
const latestLoading = ref(false);

const readLatestCache = () => {
  try {
    const raw = localStorage.getItem(LATEST_CACHE_KEY);

    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (
      !parsed ||
      !parsed.product ||
      typeof parsed.savedAt !== 'number'
    ) {
      return null;
    }

    return parsed.product;
  } catch {
    return null; /* localStorage bloqueado o JSON corrupto */
  }
};

const isLatestCacheFresh = () => {
  try {
    const raw = localStorage.getItem(LATEST_CACHE_KEY);

    if (!raw) return false;

    const parsed = JSON.parse(raw);

    return Boolean(
      parsed &&
      parsed.product &&
      typeof parsed.savedAt === 'number' &&
      Date.now() - parsed.savedAt < LATEST_CACHE_TTL
    );
  } catch {
    return false;
  }
};

const writeLatestCache = product => {
  if (!product) return;

  try {
    localStorage.setItem(
      LATEST_CACHE_KEY,
      JSON.stringify({ product, savedAt: Date.now() })
    );
  } catch {
    /* sin localStorage: el menú igual funciona, sólo se pide siempre */
  }
};

const loadLatestProduct = async () => {
  if (latestProduct.value) return;

  /* Cache fresca: se usa al toque, sin tocar la API */
  if (isLatestCacheFresh()) {
    latestProduct.value = readLatestCache();
    return;
  }

  latestLoading.value = true;

  try {
    /* Import dinámico a propósito: traer el servicio al bundle inicial
       arrastra apiClient → router → Home.vue y rompe el arranque de la app */
    const { getProductsPaginated } = await import('@/services/products');
    const response = await getProductsPaginated(1, 1);
    latestProduct.value = (response.results || [])[0] || null;

    writeLatestCache(latestProduct.value);
  } catch {
    /* Si falla, se muestra lo último cacheado (aunque esté vencido) */
    latestProduct.value = readLatestCache();
  } finally {
    latestLoading.value = false;
  }
};



/* Al abrir: se bloquea el scroll del fondo y se carga la último producto */
watch(isMenuOpen, isOpen => {
  document.body.style.overflow = isOpen ? 'hidden' : '';

  if (isOpen) {
    loadLatestProduct();
  }
});


/* =========================================================
   SCROLL
   ========================================================= */

const handleScroll = () => {
  if (
    showSearch.value ||
    isMenuOpen.value
  ) {
    scrolled.value = true;
    return;
  }

  scrolled.value =
    window.scrollY > 50;
};


/* =========================================================
   KEYBOARD
   ========================================================= */

const handleKeydown = event => {
  if (event.key !== 'Escape') {
    return;
  }

  if (showSearch.value) {
    closeSearch();
  }

  if (isMenuOpen.value) {
    closeMenu();
  }
};


/* =========================================================
   LIFECYCLE
   ========================================================= */

onMounted(() => {
  storedUsername.value =
    localStorage.getItem('username') || '';

  handleScroll();

  window.addEventListener(
    'scroll',
    handleScroll,
    {
      passive: true
    }
  );

  window.addEventListener(
    'keydown',
    handleKeydown
  );
});


onUnmounted(() => {
  clearTimeout(searchTimer);
  searchRequestId += 1;

  window.removeEventListener(
    'scroll',
    handleScroll
  );

  window.removeEventListener(
    'keydown',
    handleKeydown
  );
});
</script>

<style scoped>
/* =========================================================
   HEADER
   ========================================================= */

header {
  z-index: 2000;

  /* Alto de la marquee: lo usan el propio strip y el menú mobile */
  --marquee-h: 34px;
}


/* =========================================================
   NAVBAR
   ========================================================= */

.navbar {
  width: 100%;

  padding: 0.35rem 0;

  background: transparent;

  transition:
    background 0.3s ease,
    backdrop-filter 0.3s ease,
    box-shadow 0.3s ease;
}


.navbar-scrolled {
  background: rgba(0, 0, 0, 0.92);

  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);

  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.07);
}


/* =========================================================
   DESKTOP DISTRIBUTION
   ========================================================= */

.desktop-navbar {
  justify-content: space-between;
}


/* =========================================================
   LINKS
   ========================================================= */

.nav-link {
  position: relative;

  padding: 0.5rem 0 !important;

  margin-right: 2rem;

  color: rgba(255, 255, 255, 0.86) !important;

  font-size: 0.83rem;
  font-weight: 600;

  letter-spacing: 0.06em;

  transition:
    opacity 0.25s ease,
    color 0.25s ease;
}


.nav-link::after {
  content: '';

  position: absolute;

  left: 0;
  bottom: 0;

  width: 100%;
  height: 1px;

  background: #ffffff;

  transform: scaleX(0);

  transform-origin: right;

  transition:
    transform 0.3s ease;
}


.nav-link:hover {
  color: #ffffff !important;

  opacity: 1;
}


.nav-link:hover::after {
  transform: scaleX(1);

  transform-origin: left;
}


/* =========================================================
   DERECHA
   ========================================================= */

.nav-actions {
  display: flex;

  align-items: center;

  justify-content: flex-end;

  gap: 0.65rem;

  margin-left: auto;
}


/* =========================================================
   ICON BUTTON
   ========================================================= */

.nav-icon-btn {
  width: 42px;
  height: 42px;

  display: inline-flex;

  align-items: center;
  justify-content: center;

  padding: 0;

  border: 0;

  border-radius: 50%;

  background: transparent;

  color: #ffffff;

  font-size: 0.95rem;

  text-decoration: none;

  cursor: pointer;

  opacity: 0.88;

  transition:
    opacity 0.25s ease,
    background 0.25s ease;
}


.nav-icon-btn:hover {
  opacity: 1;

  background:
    rgba(255, 255, 255, 0.08);
}


/* =========================================================
   ACCOUNT
   ========================================================= */

.account-btn {
  min-height: 42px;

  display: inline-flex;

  align-items: center;
  justify-content: center;

  gap: 0.6rem;

  padding: 0 1rem;

  border:
    1px solid rgba(255, 255, 255, 0.22);

  border-radius: 0;

  background:
    rgba(255, 255, 255, 0.03);

  color: #ffffff;

  font-size: 0.72rem;

  font-weight: 600;

  letter-spacing: 0.08em;

  text-decoration: none;

  transition:
    background 0.25s ease,
    border-color 0.25s ease;
}


.account-btn:hover {
  color: #ffffff;

  background:
    rgba(255, 255, 255, 0.09);

  border-color:
    rgba(255, 255, 255, 0.45);
}


/* =========================================================
   BUSCADOR A PANTALLA COMPLETA (mismo criterio que el menú)
   ========================================================= */

.search-menu {
  position: fixed;

  inset: 0;

  /* Debajo del header fijo, igual que el menú de navlinks */
  z-index: -1;

  display: flex;
  flex-direction: column;

  padding-top: calc(var(--nav-h, 85px) + var(--marquee-h));

  overflow-y: auto;

  background: #000000;
}


.search-menu-container {
  flex: 1 1 auto;

  display: grid;
  align-content: start;

  gap: clamp(1.25rem, 3vh, 2rem);

  width: 100%;
  max-width: calc(var(--container-width, 1200px) + 2rem);

  margin-inline: auto;

  padding: clamp(1.25rem, 3vh, 2rem) 1rem clamp(2rem, 5vh, 3rem);

  border-top: 1px solid rgba(255, 255, 255, 0.07);
}


/* CAMPO */

.site-menu-search {
  display: flex;
  align-items: center;

  gap: 1rem;

  border-bottom: 1px solid rgba(255, 255, 255, 0.2);

  transition: border-color 0.25s ease;
}

.site-menu-search:focus-within {
  border-color: #ffffff;
}

.site-menu-search-icon {
  color: rgba(255, 255, 255, 0.45);
}

.site-menu-search-input {
  flex: 1 1 auto;

  padding: 1rem 0;

  border: 0;
  background: none;

  color: #ffffff;

  font-size: clamp(1.3rem, 3.2vw, 2.4rem);
  font-weight: 600;
  letter-spacing: -0.01em;
  text-transform: uppercase;
}

.site-menu-search-input::placeholder {
  color: rgba(255, 255, 255, 0.28);
}

.site-menu-search-input:focus {
  outline: none;
}

.site-menu-search-close {
  display: inline-flex;

  padding: 0.4rem;

  border: 0;
  background: none;

  color: rgba(255, 255, 255, 0.7);

  cursor: pointer;
}

.site-menu-search-close:hover {
  color: #ffffff;
}


/* TEXTOS */

.search-menu-helper,
.search-menu-empty {
  margin: 0;

  color: rgba(255, 255, 255, 0.5);

  font-size: 0.85rem;
}

.search-menu-empty strong {
  color: #ffffff;
}

.search-menu-sub {
  margin: 0 0 1rem;

  color: rgba(255, 255, 255, 0.45);

  font-size: 0.58rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}


/* RESULTADOS */

.search-menu-results {
  display: grid;
}

.search-menu-list {
  margin: 0;
  padding: 0;

  list-style: none;

  border-top: 1px solid rgba(255, 255, 255, 0.09);
}

.search-menu-item {
  display: flex;
  align-items: center;

  gap: 1.25rem;

  padding: 0.8rem 0;

  border-bottom: 1px solid rgba(255, 255, 255, 0.09);

  color: #ffffff;
  text-decoration: none;

  transition: opacity 0.25s ease;
}

.search-menu-item:hover {
  opacity: 0.7;
}

.search-menu-item-media {
  flex: 0 0 auto;

  width: 56px;
  height: 56px;

  overflow: hidden;

  border: 1px solid rgba(255, 255, 255, 0.12);
  background: #0a0a0a;
}

.search-menu-item-media img {
  display: block;

  width: 100%;
  height: 100%;

  object-fit: cover;
}

.search-menu-item-name {
  flex: 1 1 auto;

  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: uppercase;
}

.search-menu-item-arrow {
  color: rgba(255, 255, 255, 0.5);
}

.search-menu-all {
  justify-self: start;

  display: inline-flex;
  align-items: center;

  gap: 0.7rem;

  margin-top: 1.75rem;

  padding: 1rem 1.6rem;

  border: 1px solid #ffffff;
  background: #ffffff;

  color: #000000;

  font-size: 0.62rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;

  cursor: pointer;
}


/* ENTRADA: mismo criterio que el menú (blurreado → nítido) */

.site-menu-search,
.search-menu-helper,
.search-menu-sub,
.search-menu-empty,
.search-menu-all {
  animation: site-menu-item-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
}

.search-menu-list li:nth-child(-n+6) {
  animation: site-menu-item-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards;
}

.search-menu-list li:nth-child(1) { animation-delay: 0.05s; }
.search-menu-list li:nth-child(2) { animation-delay: 0.1s; }
.search-menu-list li:nth-child(3) { animation-delay: 0.15s; }
.search-menu-list li:nth-child(4) { animation-delay: 0.2s; }
.search-menu-list li:nth-child(5) { animation-delay: 0.25s; }
.search-menu-list li:nth-child(6) { animation-delay: 0.3s; }

.search-menu-all {
  animation-delay: 0.32s;
}

@media (max-width: 991.98px) {
  .search-menu {
    --nav-h: 76px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .site-menu-search,
  .search-menu-helper,
  .search-menu-sub,
  .search-menu-empty,
  .search-menu-all,
  .search-menu-list li {
    animation: none;
  }
}
/* =========================================================
   SEARCH ANIMATION
   ========================================================= */

.search-panel-enter-active,
.search-panel-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.4s
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      );
}


.search-panel-enter-from,
.search-panel-leave-to {
  opacity: 0;

  transform:
    translateY(-15px);
}


/* =========================================================
   MOBILE
   ========================================================= */

.mobile-actions {
  gap: 0.35rem;

  justify-content: space-between;
}


/* Acciones de la derecha (buscar + cuenta) */
.mobile-actions-right {
  display: flex;
  align-items: center;

  gap: 0.35rem;
}


/* La hamburguesa va a la izquierda: sin el corrimiento del desktop */
.mobile-actions .menu-toggle,
.mobile-actions .mobile-menu-toggle {
  margin-left: 0;

  order: -1;
}


.menu-toggle {
  position: relative;

  width: 38px;
  height: 38px;

  margin-left: 0.25rem;

  padding: 0;

  border: none;

  background: none;

  cursor: pointer;
}


.menu-toggle span {
  position: absolute;

  left: 7px;

  display: block;

  width: 24px;
  height: 1px;

  background: #ffffff;

  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}


.menu-toggle span:nth-child(1) {
  top: 11px;
}


.menu-toggle span:nth-child(2) {
  top: 18px;
}


.menu-toggle span:nth-child(3) {
  top: 25px;
}


.menu-toggle.is-active
span:nth-child(1) {
  transform:
    translateY(7px)
    rotate(45deg);
}


.menu-toggle.is-active
span:nth-child(2) {
  opacity: 0;
}


.menu-toggle.is-active
span:nth-child(3) {
  transform:
    translateY(-7px)
    rotate(-45deg);
}


/* =========================================================
   MOBILE MENU
   ========================================================= */

.mobile-menu {
  /* Alto del navbar (sin la marquee): el contenido arranca debajo del header */
  --nav-h: 85px;

  position: fixed;

  inset: 0;

  /* Debajo del header fijo: la hamburguesa (ahora X) queda clickeable */
  z-index: -1;

  display: flex;
  flex-direction: column;

  /* Sin padding acá: el recuadro que sube tiene que ir del borde inferior al
     superior de la pantalla. El hueco del header va en el container. */

  overflow-y: auto;

  background: #000000;
}


.mobile-menu-container {
  flex: 1 1 auto;

  /* Contexto para la marca de agua (logo gigante abajo a la izquierda) */
  position: relative;

  display: grid;
  grid-template-rows: 1fr auto;

  gap: clamp(1.5rem, 4vh, 3rem);

  width: 100%;
  max-width: calc(var(--container-width, 1200px) + 2rem);

  margin-inline: auto;

  padding: calc(var(--nav-h) + var(--marquee-h) + clamp(1.5rem, 4vh, 2.5rem)) 1rem clamp(1.5rem, 4vh, 2.5rem);

  border-top: 1px solid rgba(255, 255, 255, 0.07);
}


/* MARCA DE AGUA: logo gigante, al 10% y girado 40° en la esquina inferior izq.
   Va en una capa propia con overflow hidden para que el logo pueda sangrar
   fuera del recuadro sin agrandar el scroll del menú (y para que la cortina
   de entrada la revele junto con el resto del contenido). */

.site-menu-watermark-layer {
  position: absolute;

  inset: 0;

  z-index: 0;

  overflow: hidden;

  pointer-events: none;
}


.site-menu-watermark {
  position: absolute;
  /* centrado */
  left: 50%;
  transform: translateX(-50%);
  bottom: 18%;

  width: clamp(420px, 62vw, 1550px);
  max-width: none;
  height: auto;

  opacity: 0.1;
  filter: brightness(0.4) contrast(1.6);

  /* transform: rotate(40deg); */

  pointer-events: none;
  user-select: none;
}

@media (max-width: 991.98px) {
  .site-menu-watermark {
    top: 30%;
  }
}


/* El contenido del menú entra dentro del recuadro que sube hasta el tope,
   igual que una página nueva (mismos keyframes globales de App.vue). */
.mobile-menu-container--in {
  animation: page-reveal-up var(--curtain-salida, 620ms) cubic-bezier(0.33, 1, 0.68, 1) both;
}


/* NAVLINKS GRANDES, CENTRADOS */

.site-menu-main {
  /* Por encima de la marca de agua */
  position: relative;
  z-index: 1;

  display: grid;

  align-content: center;
  justify-items: center;

  gap: clamp(1.5rem, 4vh, 2.75rem);

  text-align: center;
}


.site-menu-links {
  display: grid;

  gap: clamp(0.25rem, 1.2vh, 0.9rem);

  margin: 0;
  padding: 0;

  list-style: none;
}


.site-menu-link {
  display: inline-block;

  color: #ffffff;

  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.05;
  text-decoration: none;

  transition: opacity 0.25s ease, text-shadow 0.45s ease-out;
}


.site-menu-link:hover {
  opacity: 0.6;
}



/* =========================================================
   ENTRADA DEL MENÚ: de blurreado a nítido, escalonado
   ========================================================= */

@keyframes site-menu-item-in {
  from {
    opacity: 0;
    filter: blur(16px);
    transform: translateY(14px);
  }

  to {
    opacity: 1;
    filter: blur(0);
    transform: translateY(0);
  }
}


.site-menu-links li {
  animation: site-menu-item-in 0.62s cubic-bezier(0.22, 1, 0.36, 1) backwards;
}


.site-menu-links li:nth-child(1) { animation-delay: 0.06s; }
.site-menu-links li:nth-child(2) { animation-delay: 0.14s; }
.site-menu-links li:nth-child(3) { animation-delay: 0.22s; }
.site-menu-links li:nth-child(4) { animation-delay: 0.3s; }



.site-menu-note {
  animation: site-menu-item-in 0.6s cubic-bezier(0.22, 1, 0.36, 1) backwards;
  animation-delay: 0.5s;
}


.site-menu-latest {
  animation: site-menu-item-in 0.6s cubic-bezier(0.22, 1, 0.36, 1) backwards;
  animation-delay: 0.6s;
}


@media (prefers-reduced-motion: reduce) {
  .site-menu-links li,
  .site-menu-note,
  .site-menu-latest,
  .slide-down-leave-active {
    animation: none;
  }
}



/* PIE: TEXTO A LA IZQUIERDA, ÚLTIMO PRODUCTO A LA DERECHA */

.site-menu-bottom {
  /* Por encima de la marca de agua */
  position: relative;
  z-index: 1;

  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  gap: 2rem;
}


.site-menu-note {
  max-width: 38ch;

  margin: 0;

  color: rgba(255, 255, 255, 0.5);

  font-size: 0.78rem;
  line-height: 1.7;
}


.site-menu-latest {
  display: flex;
  align-items: center;

  gap: 1.25rem;

  padding: 0.75rem;

  border: 1px solid rgba(255, 255, 255, 0.14);

  text-decoration: none;

  transition: border-color 0.25s ease;
}


.site-menu-latest:hover {
  border-color: rgba(255, 255, 255, 0.45);
}


.site-menu-latest-copy {
  display: grid;

  gap: 0.35rem;
}


.site-menu-latest-kicker {
  color: rgba(255, 255, 255, 0.45);

  font-size: 0.55rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}


.site-menu-latest-name {
  color: #ffffff;

  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: uppercase;
}


.site-menu-latest-img {
  display: block;

  width: 96px;
  height: 66px;

  object-fit: cover;
  object-position: top;
}




/* Skeleton de la último producto (mismas medidas que la tarjeta real) */

.site-menu-latest--skeleton {
  pointer-events: none;
}

.site-menu-skeleton {
  display: block;

  background: rgba(255, 255, 255, 0.07);

  animation: menu-skeleton-pulse 1.4s ease-in-out infinite;
}

.site-menu-skeleton--kicker {
  width: 6rem;
  height: 0.55rem;
}

.site-menu-skeleton--name {
  width: 8.5rem;
  height: 1.05rem;
}

.site-menu-skeleton--img {
  width: 96px;
  height: 66px;
}

@keyframes menu-skeleton-pulse {
  0%,
  100% {
    opacity: 0.45;
  }

  50% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .site-menu-skeleton {
    animation: none;
  }
}


@media (max-width: 991.98px) {
  .mobile-menu {
    --nav-h: 76px;
  }
}


@media (max-width: 760px) {
  .site-menu-bottom {
    flex-direction: column;
    align-items: stretch;
  }

  .site-menu-note {
    max-width: none;
  }

  .site-menu-latest {
    justify-content: space-between;
  }
}


/* =========================================================
   HOVER DEL MENÚ: el ítem apuntado brilla y el resto se atenúa
   Sólo se activa con el mouse sobre un ítem/bloque real (gracias a :has):
   pasar por el contenedor, su padding o los huecos no atenúa nada.
   ========================================================= */

.site-menu-links li,
.site-menu-sub li,
.site-menu-bottom > * {
  transition:
    opacity 0.35s ease,
    border-color 0.35s ease;
}

.site-menu-sublink {
  transition: text-shadow 0.45s ease-out;
}

/* Navlinks: el ítem bajo el mouse queda a full y el resto baja */
.site-menu-links:has(li:hover) li:not(:hover),
.site-menu-sub:has(li:hover) li:not(:hover) {
  opacity: 0.28;
}

.site-menu-links li:hover .site-menu-link {
  text-shadow:
    0 0 12px rgba(255, 255, 255, 0.22);
}

.site-menu-sub li:hover .site-menu-sublink {
  text-shadow:
    0 0 9px rgba(255, 255, 255, 0.18);
}

/* Bloques del menú (nav y pie): sólo si el mouse está sobre uno de ellos */
.mobile-menu-container:has(> *:hover) > *:not(:hover):not(.site-menu-watermark-layer) {
  opacity: 0.34;
}

/* En el pie: la nota y la tarjeta de la último producto también se destacan */
.site-menu-bottom:has(> *:hover) > *:not(:hover) {
  opacity: 0.45;
}

.site-menu-bottom:hover .site-menu-note:hover {
  color: #ffffff;

  text-shadow:
    0 0 12px rgba(255, 255, 255, 0.16);
}

.site-menu-bottom:hover .site-menu-latest:hover {
  border-color:
    rgba(255, 255, 255, 0.38);

  box-shadow:
    0 0 18px rgba(255, 255, 255, 0.05);
}


/* =========================================================
   MOBILE
   ========================================================= */

@media (max-width: 991.98px) {
  .navbar {
    padding:
      0.25rem 0;
  }


  .navbar .container {
    padding-top:
      0.8rem !important;

    padding-bottom:
      0.8rem !important;
  }


  .search-menu-container {
    padding:
      1.6rem
      1.25rem
      2rem;
  }


  .search-menu-input {
    font-size:
      clamp(
        1.5rem,
        7vw,
        2.2rem
      );
  }


  .search-result-image-wrapper {
    width: 48px;
    height: 48px;
  }
}


/* =========================================================
   TRANSITIONS
   ========================================================= */

/* El cierre es la cortina al revés: el mismo recuadro del revelado de sección,
   pero yéndose para arriba (el borde inferior sube hasta el tope). Mismos
   keyframes globales de App.vue, invertidos. */
.slide-down-leave-active {
  animation: page-reveal-out-up var(--curtain-salida, 480ms) cubic-bezier(0.65, 0, 0.35, 1) both;

  pointer-events: none;
}


.slide-down-leave-to {
  opacity: 1;

  transform: none;
}


@keyframes page-reveal-out-up {
  from {
    clip-path: inset(0 0 0 0);
  }

  to {
    clip-path: inset(0 0 100% 0);
  }
}


/* La entrada del menú la hace el recuadro que sube: el overlay aparece
   directo, sin su propio fundido, para no pisar la animación. */
.slide-down-enter-active {
  transition: none;
}


.slide-down-enter-from {
  opacity: 1;

  transform: none;
}


/* =========================================================
   MARQUEE
   ========================================================= */

.marquee {
  position: relative;

  width: 100%;
  height: var(--marquee-h);

  overflow: hidden;

  background: #ffffff;

  border-bottom:
    1px solid rgba(0, 0, 0, 0.12);

  /* Difumina las puntas para que el loop no tenga costura */
  -webkit-mask-image:
    linear-gradient(
      to right,
      transparent 0,
      #000 26px,
      #000 calc(100% - 26px),
      transparent 100%
    );

  mask-image:
    linear-gradient(
      to right,
      transparent 0,
      #000 26px,
      #000 calc(100% - 26px),
      transparent 100%
    );
}


.marquee-track {
  display: flex;

  width: max-content;
  height: 100%;

  animation:
    marquee-run 34s linear infinite;

  will-change: transform;
}


.marquee-group {
  display: flex;

  align-items: center;

  flex: 0 0 auto;
}


.marquee-item {
  display: inline-flex;

  align-items: center;

  gap: 0.55rem;

  height: var(--marquee-h);

  padding: 0 1.4rem;

  color:
    rgba(0, 0, 0, 0.78);

  font-size: 0.62rem;
  font-weight: 700;

  letter-spacing: 0.18em;

  text-transform: uppercase;

  white-space: nowrap;
}


.marquee-sep {
  flex: 0 0 auto;

  width: 4px;
  height: 4px;

  border-radius: 50%;

  background:
    rgba(0, 0, 0, 0.4);
}


.marquee:hover
.marquee-track {
  animation-play-state: paused;
}


@keyframes marquee-run {
  from {
    transform:
      translate3d(0, 0, 0);
  }

  to {
    transform:
      translate3d(-25%, 0, 0);
  }
}


@media (prefers-reduced-motion: reduce) {
  .marquee-track {
    animation-duration: 120s;
  }
}


@media (max-width: 991.98px) {
  header {
    --marquee-h: 30px;
  }

  .marquee-item {
    padding: 0 1rem;

    font-size: 0.58rem;

    letter-spacing: 0.16em;
  }
}
</style>
